// 🪪 치과 「오늘」 — 웹 접수 목록 카드 "오늘 접수 N건" (2026-10-01 대표 확정, 기획서 부록 4 보강)
// listClinicWalkins → 시각·이름·목적·앱 등록 여부 배지("앱 연결됨"/"웹 접수만"). 호명용. 30초 자동 새로고침.
// 미연결 행 [연결] → 명부 환자(확인 대기·정상) 선택 모달 → linkClinicWalkin({walkinId,pid}) (계약 §B-5 수동 연결, 10-01 최종 검토 P1-1).
// 부모(ClinicToday)가 플래그 clinicWalkin(단독) ON 일 때만 마운트 → OFF 면 호출 0.
// 콘솔에 환자명·번호 기록 금지 — 오류 로그는 코드까지만. 인쇄·복사·내보내기 버튼 없음.
import React, { useState, useEffect, useCallback } from 'react';
import { UserCheck } from 'lucide-react';
import { listClinicWalkins, linkClinicWalkin } from '../../services/clinicWalkinApi';
import { walkinPurposeLabel, walkinListErrorMessage, walkinLinkErrorMessage, formatWalkinTime } from '../../utils/clinicWalkin';
import { listClinicPatientRecords, describeError } from '../../services/clinicPatientsApi';

const REFRESH_MS = 30 * 1000;

function ClinicWalkinCard({ clinicId }) {
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [err, setErr] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [off, setOff] = useState(false);   // 서버 feature-off → 카드 숨김
    const [truncated, setTruncated] = useState(false);   // 서버 상한(최근 300건) 초과 — 계약 §F P2-5
    const [linkTarget, setLinkTarget] = useState(null);  // [연결] 모달 대상 접수 { walkinId, name }
    const [linkBusy, setLinkBusy] = useState(false);     // 연결 호출 중(2중 클릭 가드)
    const [linkErr, setLinkErr] = useState('');

    const load = useCallback(async () => {
        setErr('');
        try {
            const d = await listClinicWalkins();
            const list = Array.isArray(d.items) ? d.items : [];
            setItems(list);
            setCount(Number.isFinite(Number(d.count)) ? Number(d.count) : list.length);
            setTruncated(d.truncated === true);
            setOff(false);
        } catch (e) {
            if (e?.details?.code === 'feature-off') { setOff(true); setItems([]); setCount(0); }
            else {
                console.error('[웹 접수] 목록 조회 실패:', e?.code || e?.message || e);
                setErr(walkinListErrorMessage(e));
            }
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, REFRESH_MS);
        return () => clearInterval(t);
    }, [load]);

    // 수동 연결 — 성공하면 해당 행만 '앱 연결됨'으로 바꾸고 모달을 닫는다(멱등 alreadyLinked 도 성공)
    const doLink = async (pid) => {
        if (!linkTarget || linkBusy) return;
        setLinkBusy(true); setLinkErr('');
        try {
            const r = await linkClinicWalkin(linkTarget.walkinId, pid);
            if (r?.ok !== true) {
                console.error('[웹 접수] 연결 응답 이상: ok=', r?.ok);
                setLinkErr('연결하지 못했습니다. 잠시 후 다시 시도해 주세요.');
                return;
            }
            setItems(prev => prev.map(it => it.walkinId === linkTarget.walkinId ? { ...it, linked: true, linkedRecordId: pid } : it));
            setLinkTarget(null);
        } catch (e) {
            console.error('[웹 접수] 수동 연결 실패:', e?.code || e?.message || e, e?.details?.code || '');
            setLinkErr(walkinLinkErrorMessage(e));
        } finally { setLinkBusy(false); }
    };

    if (!loaded || off) return null;

    return (
        <div style={S.card}>
            <div style={S.head}>
                <div style={S.iconBox}><UserCheck size={18} color="#fff" /></div>
                <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={S.title}>{`오늘 접수 ${count}건`}</div>
                    <div style={S.notice}>데스크 QR로 접수한 환자입니다. 이름을 보고 호명해 주세요. 30초마다 새로 고칩니다.</div>
                </div>
                <button type="button" onClick={load} style={S.chip}>새로 고침</button>
            </div>
            {err && <div style={S.err}>{err}</div>}
            {!err && truncated && <div style={S.truncated}>접수가 많아 일부만 보입니다.</div>}
            {!err && items.length === 0 && <div style={S.empty}>아직 접수한 환자가 없습니다.</div>}
            {items.length > 0 && (
                <div style={S.rows}>
                    {items.map((it, i) => {
                        const linked = it.linked === true || it.appLinked === true;
                        return (
                            <div key={it.walkinId || i} style={S.row}>
                                <span style={S.time}>{formatWalkinTime(it.submittedAt)}</span>
                                <span style={S.name}>{String(it.name || '').trim() || '-'}</span>
                                <span style={S.purpose}>{walkinPurposeLabel(it.purpose)}</span>
                                <span style={{ ...S.badge, ...(linked ? S.badgeLinked : S.badgeWeb) }}>{linked ? '앱 연결됨' : '웹 접수만'}</span>
                                {!linked && clinicId && (
                                    <button type="button" onClick={() => { setLinkErr(''); setLinkTarget({ walkinId: it.walkinId, name: String(it.name || '').trim() }); }}
                                        title="앱 등록(명부) 환자와 이 접수를 수동으로 연결" style={S.linkBtn}>연결</button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            {linkTarget && (
                <WalkinLinkModal clinicId={clinicId} walkin={linkTarget} busy={linkBusy} error={linkErr}
                    onPick={doLink} onClose={() => { if (!linkBusy) setLinkTarget(null); }} />
            )}
        </div>
    );
}

// [연결] 선택 모달 — 명부(clinicPatientRecords)에서 확인 대기(unconfirmed)·정상(active) 환자만 고른다(deleteRequested·deleted 제외).
//  기본 검색어 = 접수 이름(같은 이름을 먼저 보이게). 명부 조회는 기존 listClinicPatientRecords 재사용(새 쿼리 경로 없음).
function WalkinLinkModal({ clinicId, walkin, busy, error, onPick, onClose }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadErr, setLoadErr] = useState('');
    const [term, setTerm] = useState(walkin?.name || '');

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const all = await listClinicPatientRecords(clinicId);
                if (alive) setRows(all.filter(r => r.status === 'unconfirmed' || r.status === 'active'));
            } catch (e) {
                if (alive) setLoadErr('환자 명부를 불러오지 못했습니다: ' + describeError(e));
            } finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [clinicId]);

    const norm = (v) => String(v || '').replace(/\s+/g, '').toLowerCase();
    const q = norm(term);
    const list = (q ? rows.filter(r => norm(r.name).includes(q) || (r.phoneLast4 || '').includes(q)) : rows).slice(0, 50);

    return (
        <div style={M.overlay} onClick={onClose}>
            <div style={M.box} onClick={(e) => e.stopPropagation()}>
                <div style={M.title}>{`접수 연결 — ${walkin?.name || '-'}`}</div>
                <div style={M.help}>이 접수를 연결할 환자를 명부에서 고르세요. 확인 대기(앱 등록) 또는 정상 환자만 보입니다.</div>
                <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="이름 또는 휴대폰 뒤 4자리" style={M.input} />
                {error && <div style={M.err}>{error}</div>}
                {loadErr && <div style={M.err}>{loadErr}</div>}
                <div style={M.list}>
                    {loading ? <div style={M.empty}>불러오는 중…</div>
                        : list.length === 0 ? <div style={M.empty}>{rows.length === 0 ? '명부에 연결할 수 있는 환자가 없습니다.' : '검색 결과가 없습니다.'}</div>
                        : list.map(r => (
                            <button key={r.id} type="button" disabled={busy} onClick={() => onPick(r.id)} style={{ ...M.item, opacity: busy ? 0.6 : 1 }}>
                                <span style={{ fontWeight: 700, color: '#0f172a' }}>{r.name || '(이름 없음)'}</span>
                                <span style={{ color: '#64748b', fontSize: 12.5 }}>
                                    {r.birthDate || (r.birthYear ? `${r.birthYear}년` : '')}{r.phoneLast4 ? ` · ***-${r.phoneLast4}` : ''}
                                </span>
                                <span style={{ ...M.badge, ...(r.status === 'unconfirmed' ? M.badgePending : M.badgeActive) }}>{r.status === 'unconfirmed' ? '확인 대기' : '정상'}</span>
                            </button>
                        ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                    <button type="button" disabled={busy} onClick={onClose} style={M.btnS}>{busy ? '연결 중…' : '닫기'}</button>
                </div>
            </div>
        </div>
    );
}

const M = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
    box: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460, padding: '16px 18px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' },
    title: { fontSize: 15, fontWeight: 700, color: '#0f172a' },
    help: { fontSize: 12.5, color: '#64748b', marginTop: 4, lineHeight: 1.5 },
    input: { width: '100%', boxSizing: 'border-box', marginTop: 10, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' },
    err: { marginTop: 8, background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
    list: { marginTop: 8, maxHeight: 320, overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: 8 },
    empty: { padding: 14, fontSize: 13, color: '#94a3b8', textAlign: 'center' },
    item: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 12px', background: '#fff', border: 'none', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' },
    badge: { marginLeft: 'auto', borderRadius: 6, padding: '2px 8px', fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap' },
    badgePending: { background: '#ecfeff', color: '#0e7490' },
    badgeActive: { background: '#dcfce7', color: '#166534' },
    btnS: { padding: '7px 14px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
};

const S = {
    card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', marginBottom: 14, userSelect: 'none' },
    head: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
    iconBox: { width: 36, height: 36, borderRadius: 10, background: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    title: { fontSize: 15, fontWeight: 700, color: '#0f172a' },
    notice: { fontSize: 12, color: '#64748b', marginTop: 2, lineHeight: 1.5 },
    chip: { padding: '6px 12px', fontSize: 12.5, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#475569', cursor: 'pointer' },
    err: { marginTop: 10, background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
    truncated: { marginTop: 10, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
    empty: { marginTop: 10, fontSize: 13, color: '#94a3b8', padding: '6px 2px' },
    rows: { marginTop: 10, display: 'flex', flexDirection: 'column' },
    row: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 4px', borderBottom: '1px solid #f1f5f9', fontSize: 14 },
    time: { color: '#64748b', fontSize: 13, minWidth: 72 },
    name: { fontWeight: 700, color: '#0f172a', fontSize: 16, minWidth: 80 },
    purpose: { color: '#475569', fontSize: 13.5 },
    badge: { marginLeft: 'auto', borderRadius: 6, padding: '3px 9px', fontSize: 12, fontWeight: 600 },
    badgeLinked: { background: '#dcfce7', color: '#166534' },
    badgeWeb: { background: '#f1f5f9', color: '#475569' },
    linkBtn: { padding: '4px 10px', borderRadius: 999, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
};

export default ClinicWalkinCard;
